import type { FragmentOf } from "@opensea/graphql"
import { readFragment } from "@opensea/graphql"
import {
  BottomSheetBody,
  BottomSheetFooter,
  BottomSheetHeader,
  BottomSheetHeaderTitle,
  Flex,
  FlexColumn,
  Separator,
  Spinner,
  TextBody,
} from "@opensea/ui-kit"
import { differenceInMilliseconds, isAfter, max, min, parseISO } from "date-fns"
import { useEffect, useState } from "react"
import { useTranslations } from "use-intl"
import { Button } from "../../../../../components/Button"
import type { GaslessCancelSignatureMap } from "../../types"
import { CancelOrderStatus } from "../CancelOrderFlowStatus"
import { CancelOrderTable } from "../CancelOrderTable"
import { GaslessCancelOrderStatusFragment } from "./GaslessCancelOrderStatus.fragment"

type Props = {
  action: FragmentOf<typeof GaslessCancelOrderStatusFragment>
  signatureMap?: GaslessCancelSignatureMap
  forceOnchainCancellation: () => void
}

export const GaslessCancelOrderStatus = ({
  action: actionKey,
  signatureMap: defaultSignatureMap,
  forceOnchainCancellation,
}: Props) => {
  const t = useTranslations("wallet.GaslessCancelOrderStatus")
  const [signatureMap, setSignatureMap] = useState<
    GaslessCancelSignatureMap | undefined
  >(defaultSignatureMap)
  const action = readFragment(GaslessCancelOrderStatusFragment, actionKey)
  // Count how many orderIds have a null timestamp, meaning they were immediately cancelled
  const cancelledOrdersCount = Object.values(signatureMap || {}).filter(
    timestamp => timestamp === "",
  ).length
  const cancellationRequests = Object.keys(signatureMap || {}).length
  const hasPendingCancellations =
    signatureMap && cancelledOrdersCount !== cancellationRequests
  const count = cancelledOrdersCount || cancellationRequests

  let orderIds = action.orders.map(order => order.id)

  if (hasPendingCancellations) {
    orderIds = orderIds.filter(orderId => {
      return signatureMap[orderId] !== ""
    })
  }

  useEffect(() => {
    let timeout: number | null = null
    if (signatureMap && hasPendingCancellations) {
      const timestamps = Object.values(signatureMap).filter(Boolean)
      const earliestFutureTimestamp = min(timestamps)
      const latestTimestamp = max(timestamps)

      // If the latest timestamp is in the past or there are no future timestamps remaining,
      // we can assume all orders are cancelled and clear out the timestamps to proceed to finsihed state
      if (isAfter(new Date(), latestTimestamp) || !timestamps.length) {
        setSignatureMap(
          Object.fromEntries(orderIds.map(orderId => [orderId, ""])),
        )
      } else if (isAfter(earliestFutureTimestamp, new Date())) {
        // create a timeout that will clear out timestamps when the earliest future timestamp is reached
        timeout = window.setTimeout(
          () => {
            const currentTime = new Date()
            const allOrderIds = Object.keys(signatureMap)
            // get orderIds that have a timestamp in the future
            const remainingOrders = allOrderIds.filter(orderId => {
              return (
                signatureMap[orderId] &&
                isAfter(parseISO(signatureMap[orderId]), currentTime)
              )
            })

            // remove the timestamp from any orders that are not remainingOrders
            setSignatureMap(
              Object.fromEntries(
                allOrderIds.map(orderId => [
                  orderId,
                  remainingOrders.includes(orderId)
                    ? signatureMap[orderId]
                    : "",
                ]),
              ),
            )
          },
          differenceInMilliseconds(earliestFutureTimestamp, new Date()),
        )
      }
    }

    return () => {
      if (timeout) {
        window.clearTimeout(timeout)
      }
    }
  }, [signatureMap, hasPendingCancellations, orderIds])

  // If the signatureMap is provided and cancelledOrdersCount === cancellationRequests
  // we can proceed to the finished state
  if (signatureMap && !hasPendingCancellations) {
    return <CancelOrderStatus orders={action.orders} status="success" />
  }

  return (
    <>
      <BottomSheetHeader>
        <BottomSheetHeaderTitle asChild className="flex items-center gap-2">
          <Spinner />
          <h3>
            {action.orders[0].side === "LISTING"
              ? t("cancellingListings", { count })
              : t("cancellingOffers", { count })}
          </h3>
        </BottomSheetHeaderTitle>
      </BottomSheetHeader>
      <BottomSheetBody>
        <FlexColumn className="w-full gap-4">
          {signatureMap ? (
            cancelledOrdersCount ? (
              <TextBody size="sm">
                {action.orders[0].side === "LISTING"
                  ? t("immediateCancellationListings", {
                      count: cancelledOrdersCount,
                    })
                  : t("immediateCancellationOffers", {
                      count: cancelledOrdersCount,
                    })}
              </TextBody>
            ) : (
              <TextBody size="sm">
                {action.orders[0].side === "LISTING"
                  ? t("pendingCancellationListings", { count })
                  : t("pendingCancellationOffers", { count })}
              </TextBody>
            )
          ) : null}
          <CancelOrderTable
            filteredOrderIds={orderIds}
            orders={action.orders}
            view="done"
          />
        </FlexColumn>
        <Separator />
      </BottomSheetBody>
      <BottomSheetFooter>
        <Flex className="justify-end">
          <Button onClick={forceOnchainCancellation} variant="secondary">
            {t("cancelWithGas")}
          </Button>
        </Flex>
      </BottomSheetFooter>
    </>
  )
}
